// src/Muestras/Drawer/DrawerMuestraV2.jsx
import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Drawer,
  Box,
  CircularProgress,
  DialogActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Tooltip,
  Typography,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Divider,
  Stack,
} from "@mui/material";
import DatePicker from "react-datepicker";
import { es } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme, alpha } from "@mui/material/styles";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";

import { jwtDecode } from "jwt-decode";

import "./drawer.css";

import FileThumbnails from "../../Estimates/FileThumbnails";

import PrintEtiqueta from "../PrintEtiqueta";
import useMuestraData from "./useMuestraData";
import {
  formatMuestraDateForAPI,
  parseMuestraDate,
} from "../../../utils/dateUtils";

import HeaderSection from "./sections/HeaderSection";
import GeneralSection from "./sections/GeneralSection";
import DatesSection from "./sections/DatesSection";
import EscandallosSection from "./sections/EscandallosSection";
import MovimientosSection from "./sections/MovimientosSection";
import CourierControlMuestraLinks from "../../CourierControl/CourierControlMuestraLinks";
import SaveBar from "./sections/SaveBar";
import CalidadesPrintSection from "./sections/CalidadesPrintSection";
import PreparePhotosPreviewDialog from "./PreparePhotosPreviewDialog";
import {
  applyGeneralEditForArrived,
  findSentToClientEstadoId,
  findSentToSupplierEstadoId,
  findSdsMarcaId,
  isArrivedEstado,
  isReceptionTransition,
  isShippedPendingReceptionEstado,
  isSentToClientEstado,
  isSentToSupplierEstado,
  isShipmentMovimiento,
  requiresCourierOnShipment,
  SENT_ESTADO_ID,
} from "./muestraEstadoHelpers";
import EnvioDestinoFields, {
  emptyEnvioDestino,
  isEnvioDestinoValid,
  toMovimientoDestinoPayload,
  DESTINO_CLIENTE,
  DESTINO_PROVEEDOR,
  DESTINO_INTERNO,
} from "../EnvioDestinoFields";
import {
  shouldCopyFromFirstSample,
  findFirstSampleForProduct,
  buildMuestraFieldsFromFirst,
  buildEscandallosFromFirst,
} from "../../../utils/muestraCopyFromFirst";

import { clearCachedArchivos } from "../../../utils/cacheArchivos";
import {
  buildFotosExportConfig,
  getMuestraFileKey,
  orderMuestraMediaForDisplay,
  parseFotosExport,
  isPresentacionMuestraFile,
  pickFrontAndBackFromExport,
  resolveOriginalMuestraPhotoForPrepare,
  sanitizeFotosExport,
} from "../../../utils/muestraFotosExport";

import {
  getEscandalloSubgruposMuestras,
  deleteEscandallo,
  deleteMovimiento,
  deleteMuestra,
  getFilesByMuestraId,
  uploadFileToMuestra,
  importMeetingMediaToMuestra,
  deleteFileFromMuestra,
  addMovimiento,
  getOrderEmailAttachments,
  sendMuestraOrderEmail,
  patchMuestraFotosExport,
  prepareMuestraPhoto,
  getProductMuestras,
  getMuestraById,
  getEscandallosByMuestraId,
  updateMuestra,
} from "../../../api/Muestras/muestraApi";

import { deleteSpecificFile, uploadFile } from "../../../api/Design/fileApi";

import {
  getMuestraEmailRecipients,
  createMuestraEmailRecipient,
} from "../../../api/Muestras/muestraEmailRecipientsApi";
import { getMuestraSupplierResponsables } from "../../../api/Muestras/muestraSupplierResponsablesApi";

const normalizeEmail = (value = "") => value.trim().toLowerCase();
const parseEmailList = (value = "") =>
  value
    .split(/[;,]+/)
    .map((v) => normalizeEmail(v))
    .filter(Boolean);

const getRecipientOptionLabel = (option) =>
  typeof option === "string" ? option : option.email;

const recipientOptionEqual = (option, value) => {
  const optionEmail =
    typeof option === "string" ? normalizeEmail(option) : option.email;
  const valueEmail =
    typeof value === "string" ? normalizeEmail(value) : value.email;
  return optionEmail === valueEmail;
};

const buildRecipientValue = (rawValue, contacts) =>
  parseEmailList(rawValue).map((email) => {
    const contact = contacts.find((entry) => entry.email === email);
    return contact || email;
  });

const sortEmailContacts = (contacts) =>
  [...contacts].sort((a, b) =>
    a.email.localeCompare(b.email, undefined, { sensitivity: "base" }),
  );

export default function DrawerMuestraV2(props) {
  const {
    open,
    onSaveMuestra, // 👈 NUEVO
    onRefresh,
    onClose,
    isEditMode,
    tiposMuestra,
    tiposEstado,
    tiposTallas,
    tiposColores,
    tiposProveedores,
    tiposMarcas,
    escandalloGrupos,
    selectedProduct,
    newMuestra,
    escandallosIniciales,
    handleAddMuestraLocally,
    onDeleteMuestra,
    userCanCreateSample,
    onMuestraUpdated,
    pendingMeetingMedia = null,
    onMeetingMediaImported,
    keepOpenAfterSave = false,
    onMuestraPersisted,
    /** 'fichaPdf' desde Reuniones; 'loose' (default) desde Muestras */
    orderEmailAttachMode = "loose",
  } = props;

  const {
    loading,
    muestra,
    setMuestra,
    escandallos,
    setEscandallos,
    movimientos,
    setMovimientos,
    saveMuestra,
  } = useMuestraData({
    open,
    isEditMode,
    newMuestra,
    tiposMuestra,
    selectedProduct,
    escandallosIniciales,
  });
  const theme = useTheme();
  const { t } = useTranslation();

  const [muestraFiles, setMuestraFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [preparingPhotos, setPreparingPhotos] = useState(false);
  const [preparePhotoProgress, setPreparePhotoProgress] = useState(null);
  const [cleanPhotosPreview, setCleanPhotosPreview] = useState({
    open: false,
    items: [],
  });
  const preparingPhotosRef = useRef(false);
  const fotosExportSaveRef = useRef(null);
  const pendingFotosExportRef = useRef(null);

  const pendingMeetingMediaRef = useRef(pendingMeetingMedia);
  const filesLoadGenRef = useRef(0);
  /** Persiste modo adjuntos en el drawer (pendingMeetingMedia se limpia tras import). */
  const orderAttachModeRef = useRef(
    orderEmailAttachMode === "fichaPdf" ? "fichaPdf" : "loose",
  );
  // Snapshot síncrono: evita perder photoIds si el padre limpia el prop mid-save
  if (pendingMeetingMedia?.photoIds?.length) {
    pendingMeetingMediaRef.current = pendingMeetingMedia;
    orderAttachModeRef.current = "fichaPdf";
  }
  if (orderEmailAttachMode === "fichaPdf") {
    orderAttachModeRef.current = "fichaPdf";
  }
  useEffect(() => {
    if (pendingMeetingMedia?.photoIds?.length) {
      pendingMeetingMediaRef.current = pendingMeetingMedia;
      orderAttachModeRef.current = "fichaPdf";
    }
  }, [pendingMeetingMedia]);
  useEffect(() => {
    if (!open) {
      orderAttachModeRef.current =
        orderEmailAttachMode === "fichaPdf" ? "fichaPdf" : "loose";
    }
  }, [open, orderEmailAttachMode]);

  const effectiveOrderAttachMode =
    orderAttachModeRef.current === "fichaPdf" ||
    orderEmailAttachMode === "fichaPdf" ||
    Boolean(pendingMeetingMedia?.photoIds?.length)
      ? "fichaPdf"
      : "loose";

  const effectiveEditMode = Boolean(isEditMode || muestra?.ID_MUESTRA);

  const resolvedProductId =
    selectedProduct?.id ??
    selectedProduct?.ProductId ??
    selectedProduct?.productId ??
    selectedProduct?.ID_PRODUCTO ??
    newMuestra?.ID_PRODUCTO ??
    null;

  const handleTipoMuestraChange = useCallback(
    async ({ idTipo, abbr, newName }) => {
      if (effectiveEditMode) return;
      if (!shouldCopyFromFirstSample(abbr)) {
        return;
      }
      if (!resolvedProductId) return;

      try {
        const samples = await getProductMuestras(resolvedProductId);
        const first = findFirstSampleForProduct(samples, tiposMuestra);
        if (!first?.ID_MUESTRA) return;

        const [full, esc] = await Promise.all([
          getMuestraById(first.ID_MUESTRA),
          getEscandallosByMuestraId(first.ID_MUESTRA),
        ]);

        const escCompleto = await Promise.all(
          (esc || []).map(async (e) => {
            let subgrupos = [];
            if (e.ESCANDALLO_GRUPOS) {
              const res = await getEscandalloSubgruposMuestras(
                e.ESCANDALLO_GRUPOS,
              );
              subgrupos = (res || []).map((s) => ({
                ID_SUBGRUPO: s.ID_SUBGRUPO,
                SUBGRUPO: s.SUBGRUPO,
              }));
            }
            return {
              ...e,
              PROVEEDOR_ESCANDALLO: e.Proveedor || e.PROVEEDOR_ESCANDALLO || "",
              UN: e.Un || e.UN || "",
              COSTE: e.Coste || e.COSTE || "",
              SUBGRUPOS_LIST: subgrupos,
            };
          }),
        );

        const copied = buildMuestraFieldsFromFirst(full);
        setMuestra((prev) => ({
          ...prev,
          ...copied,
          ID_TIPO_MUESTRA: idTipo,
          MUESTRA: newName,
          ID_ESTADO: prev.ID_ESTADO || 1,
          ID_PRODUCTO: resolvedProductId,
          ID_MUESTRA: undefined,
          FECHA_PETICION: parseMuestraDate(copied.FECHA_PETICION),
          FECHA_PREVISTA: parseMuestraDate(copied.FECHA_PREVISTA),
          FECHA_GET_SUPPLIER: parseMuestraDate(copied.FECHA_GET_SUPPLIER),
          FECHA_PREVISTA_FABRICA: parseMuestraDate(
            copied.FECHA_PREVISTA_FABRICA,
          ),
          FECHA_ENVIO: null,
          FECHA_RECEPCION: null,
        }));

        const copiedEsc = buildEscandallosFromFirst(escCompleto);
        setEscandallos(
          copiedEsc.length
            ? copiedEsc
            : [{ PROVEEDOR_ESCANDALLO: "", UN: "", COSTE: "" }],
        );
      } catch (error) {
        console.error("Error copiando datos desde 1st sample:", error);
      }
    },
    [
      effectiveEditMode,
      resolvedProductId,
      tiposMuestra,
      setMuestra,
      setEscandallos,
    ],
  );

  const [deleteDialog, setDeleteDialog] = React.useState({
    open: false,
    id: null,
  });

  // ENVIAR EMAIL DE PETICIÓN DE MUESTRA AL PROVEEDOR CUANDO CAMBIA A ESTADO ORDERED
  const [orderEmailDialog, setOrderEmailDialog] = useState(false);
  const [orderEmailData, setOrderEmailData] = useState({
    to: "",
    cc: "",
  });
  const [orderEmailContacts, setOrderEmailContacts] = useState([]);
  const [orderSampleFiles, setOrderSampleFiles] = useState([]);
  const [orderSampleVideos, setOrderSampleVideos] = useState([]);
  const [orderTechnicalFiles, setOrderTechnicalFiles] = useState([]);
  const [selectedTechnicalKeys, setSelectedTechnicalKeys] = useState([]);
  const [loadingOrderAttachments, setLoadingOrderAttachments] = useState(false);
  // ENVIAR EMAIL DE PETICIÓN DE MUESTRA AL PROVEEDOR CUANDO CAMBIA A ESTADO ORDERED

  const [tiposResponsables, setTiposResponsables] = useState([]);

  const token = localStorage.getItem("token");

  let isAdmin = false;

  if (token) {
    try {
      const decoded = jwtDecode(token);
      isAdmin = decoded?.isAdmin === 1 || decoded?.isAdmin === true;
    } catch (err) {
      console.error("Error decoding token", err);
    }
  }

  useEffect(() => {
    const loadOrderEmailContacts = async () => {
      try {
        const rows = await getMuestraEmailRecipients();
        setOrderEmailContacts(
          sortEmailContacts(
            rows.map((entry) => ({
              id: entry.ID,
              email: normalizeEmail(entry.Email),
              group: entry.GroupName || "Custom",
            })),
          ),
        );
      } catch (error) {
        console.error("Error loading order email contacts", error);
      }
    };

    const loadSupplierResponsables = async () => {
      try {
        const rows = await getMuestraSupplierResponsables();
        setTiposResponsables(
          [...rows].sort((a, b) =>
            a.Nombre.localeCompare(b.Nombre, undefined, {
              sensitivity: "base",
            }),
          ),
        );
      } catch (error) {
        console.error("Error loading supplier responsables", error);
      }
    };

    loadOrderEmailContacts();
    loadSupplierResponsables();
  }, []);

  // CHINA: limitar edición de ciertos campos si el usuario tiene el límite de Proveedor activo
  const isReadOnly = props.userHasProveedorLimit === true;

  const canEditGetSupplier = isAdmin || isReadOnly;

  const [errorDialog, setErrorDialog] = React.useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmOkFactoryDialog, setConfirmOkFactoryDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [shipmentDialogOpen, setShipmentDialogOpen] = useState(false);
  /** "sent" | "sentToClient" | "sentToSupplier" */
  const [shipmentDialogMode, setShipmentDialogMode] = useState("sentToClient");
  const [sentToClientForm, setSentToClientForm] = useState({
    ...emptyEnvioDestino(),
    FECHA_ENVIO: null,
    NUM_COURIER: "",
  });
  const [savingSentToClient, setSavingSentToClient] = useState(false);
  const [confirmRevertShipmentOpen, setConfirmRevertShipmentOpen] =
    useState(false);
  const [pendingRevertEstado, setPendingRevertEstado] = useState(null);
  const [revertingShipment, setRevertingShipment] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);

  const sentToClientEstadoId = useMemo(
    () => findSentToClientEstadoId(tiposEstado),
    [tiposEstado],
  );
  const sentToSupplierEstadoId = useMemo(
    () => findSentToSupplierEstadoId(tiposEstado),
    [tiposEstado],
  );
  const shipmentLocked =
    (isSentToClientEstado(muestra?.ID_ESTADO, tiposEstado) ||
      isSentToSupplierEstado(muestra?.ID_ESTADO, tiposEstado)) &&
    !isAdmin;

  const sortedTiposProveedores = useMemo(
    () =>
      [...(tiposProveedores || [])].sort((a, b) =>
        (a.N_PROVEEDOR || "").localeCompare(b.N_PROVEEDOR || "", "es", {
          sensitivity: "base",
        }),
      ),
    [tiposProveedores],
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingEstado, setPendingEstado] = useState(null);
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [sendingOrderEmail, setSendingOrderEmail] = useState(false);
  const [saveUnknownDialogOpen, setSaveUnknownDialogOpen] = useState(false);
  const [unknownEmailsPending, setUnknownEmailsPending] = useState([]);

  const sortedOrderEmailContacts = useMemo(
    () => sortEmailContacts(orderEmailContacts),
    [orderEmailContacts],
  );

  const toRecipientValue = useMemo(
    () => buildRecipientValue(orderEmailData.to, orderEmailContacts),
    [orderEmailData.to, orderEmailContacts],
  );

  const ccRecipientValue = useMemo(
    () => buildRecipientValue(orderEmailData.cc, orderEmailContacts),
    [orderEmailData.cc, orderEmailContacts],
  );

  const handleRecipientChange = useCallback(
    (field) => (_, newValue) => {
      const emails = newValue.map((item) =>
        typeof item === "string" ? normalizeEmail(item) : item.email,
      );
      setOrderEmailData((prev) => ({
        ...prev,
        [field]: emails.join("; "),
      }));
    },
    [],
  );

  const getUnknownRecipients = () => {
    const knownEmails = new Set(orderEmailContacts.map((entry) => entry.email));
    const allTypedEmails = [
      ...parseEmailList(orderEmailData.to),
      ...parseEmailList(orderEmailData.cc),
    ];
    return [...new Set(allTypedEmails)].filter(
      (email) => !knownEmails.has(email) && email.includes("@"),
    );
  };

  const saveUnknownRecipients = async (unknownEmails) => {
    if (!unknownEmails?.length) return;
    try {
      const savedRows = await Promise.all(
        unknownEmails.map((email) =>
          createMuestraEmailRecipient({ email, group: "Custom" }),
        ),
      );

      setOrderEmailContacts((prev) => {
        const byEmail = new Map(prev.map((entry) => [entry.email, entry]));
        savedRows.forEach((row) => {
          const email = normalizeEmail(row.Email || row.email);
          byEmail.set(email, {
            id: row.ID || row.id,
            email,
            group: row.GroupName || row.group || "Custom",
          });
        });
        return sortEmailContacts(Array.from(byEmail.values()));
      });
    } catch (error) {
      console.error("Error saving unknown recipients", error);
    }
  };

  const loadOrderEmailAttachments = useCallback(async (muestraId) => {
    if (!muestraId) {
      setOrderSampleFiles([]);
      setOrderSampleVideos([]);
      setOrderTechnicalFiles([]);
      setSelectedTechnicalKeys([]);
      return;
    }

    setLoadingOrderAttachments(true);
    try {
      const data = await getOrderEmailAttachments(muestraId);
      const technical = Array.isArray(data?.technicalFiles)
        ? data.technicalFiles
        : [];
      setOrderSampleFiles(
        Array.isArray(data?.sampleFiles) ? data.sampleFiles : [],
      );
      setOrderSampleVideos(
        Array.isArray(data?.sampleVideos) ? data.sampleVideos : [],
      );
      setOrderTechnicalFiles(technical);
      // Por defecto: todas las técnicas seleccionadas (comportamiento anterior)
      setSelectedTechnicalKeys(
        technical.map((file) => file.key).filter(Boolean),
      );
    } catch (error) {
      console.error("Error loading order email attachments", error);
      setOrderSampleFiles([]);
      setOrderSampleVideos([]);
      setOrderTechnicalFiles([]);
      setSelectedTechnicalKeys([]);
    } finally {
      setLoadingOrderAttachments(false);
    }
  }, []);

  const toggleTechnicalAttachment = useCallback((key) => {
    setSelectedTechnicalKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const selectAllTechnicalAttachments = useCallback(() => {
    setSelectedTechnicalKeys(
      orderTechnicalFiles.map((file) => file.key).filter(Boolean),
    );
  }, [orderTechnicalFiles]);

  const clearTechnicalAttachments = useCallback(() => {
    setSelectedTechnicalKeys([]);
  }, []);

  const sendOrderEmailRequest = async ({ saveUnknown = false } = {}) => {
    if (sendingOrderEmail) return;
    try {
      setSendingOrderEmail(true);
      if (saveUnknown) {
        await saveUnknownRecipients(unknownEmailsPending);
      }

      await sendMuestraOrderEmail(muestra.ID_MUESTRA, {
        to: orderEmailData.to,
        cc: orderEmailData.cc,
        message: additionalMessage,
        technicalFileKeys: selectedTechnicalKeys,
        attachMode: effectiveOrderAttachMode,
      });

      setSaveUnknownDialogOpen(false);
      setUnknownEmailsPending([]);
      setOrderEmailDialog(false);
    } catch (err) {
      console.error("Error sending order email:", err);
    } finally {
      setSendingOrderEmail(false);
    }
  };

  const handleSendOrderEmail = async () => {
    const unknownEmails = getUnknownRecipients();
    if (unknownEmails.length) {
      setUnknownEmailsPending(unknownEmails);
      setSaveUnknownDialogOpen(true);
      return;
    }
    await sendOrderEmailRequest({ saveUnknown: false });
  };

  const etiquetaRef = useRef(null);

  const handlePrintEtiqueta = useReactToPrint({
    contentRef: etiquetaRef,
    documentTitle: muestra?.MUESTRA || t("Samples.labelPrintTitle"),
  });

  /*   if (!open) return null; */

  const loadSubgrupos = async (grupoId) => {
    if (!grupoId) return [];
    const res = await getEscandalloSubgruposMuestras(grupoId);
    return res.map((s) => ({
      ID_SUBGRUPO: s.ID_SUBGRUPO,
      SUBGRUPO: s.SUBGRUPO,
    }));
  };

  const openDeleteDialog = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const closeErrorDialog = () => {
    setErrorDialog({ open: false, title: "", message: "" });
  };

  const showErrorDialog = (message, title) => {
    setErrorDialog({
      open: true,
      title: title || t("Samples.errorTitle"),
      message,
    });
  };

  const handleDeleteMuestra = async () => {
    const id = deleteDialog.id;

    try {
      await deleteMuestra(id);

      // avisar al padre para que elimine la muestra de la lista
      if (onDeleteMuestra) {
        onDeleteMuestra(id);
      }

      closeDeleteDialog();
      onClose();
    } catch (err) {
      console.error("❌ Error al eliminar la muestra:", err);

      // Detectar error de FOREIGN KEY
      if (
        err?.response?.data?.message?.includes("conflicted") ||
        err?.message?.includes("ForeignKey") ||
        err?.message?.includes("REFERENCE constraint")
      ) {
        setErrorDialog({
          open: true,
          title: t("Samples.deleteBlockedTitle"),
          message: t("Samples.deleteBlockedHasRelations"),
        });
      } else {
        setErrorDialog({
          open: true,
          title: t("Samples.errorTitle"),
          message: t("Samples.deleteError"),
        });
      }

      closeDeleteDialog();
    }
  };

  // Eliminar archivo de la muestra
  const handleRequestDeleteFile = (file) => {
    setFileToDelete(file);
    setDeleteFileDialogOpen(true);
  };

  const handleConfirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      await deleteFileFromMuestra(
        muestra.ID_MUESTRA,
        fileToDelete.name,
        fileToDelete.category || undefined,
      );

      clearCachedArchivos(muestra.ID_MUESTRA);

      const files = await getFilesByMuestraId(muestra.ID_MUESTRA);
      setMuestraFiles(files || []);

      const sanitized = sanitizeFotosExport(files, muestra.FOTOS_EXPORT);
      const current = parseFotosExport(muestra.FOTOS_EXPORT);
      if (JSON.stringify(sanitized) !== JSON.stringify(current)) {
        const saved = await patchMuestraFotosExport(
          muestra.ID_MUESTRA,
          sanitized,
        );
        setMuestra((prev) => ({
          ...prev,
          FOTOS_EXPORT: saved.FOTOS_EXPORT ?? null,
        }));
      }

      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error("Error eliminando archivo:", err);
    }

    setDeleteFileDialogOpen(false);
    setFileToDelete(null);
  };

  //  subir fotos (input change o File / FileList desde drag&drop)
  const handleUploadMuestraFile = async (eventOrFiles, category) => {
    if (!muestra?.ID_MUESTRA) return;

    let files = [];
    const isInputEvent = Boolean(eventOrFiles?.target?.files);

    if (isInputEvent) {
      files = Array.from(eventOrFiles.target.files || []);
    } else if (Array.isArray(eventOrFiles)) {
      files = eventOrFiles;
    } else if (eventOrFiles instanceof FileList) {
      files = Array.from(eventOrFiles);
    } else if (eventOrFiles instanceof File) {
      files = [eventOrFiles];
    }

    files = files.filter(
      (f) =>
        f && (f.type?.startsWith("image/") || f.type?.startsWith("video/")),
    );
    if (!files.length) return;

    try {
      for (const file of files) {
        await uploadFileToMuestra(muestra.ID_MUESTRA, file, category);
      }

      clearCachedArchivos(muestra.ID_MUESTRA);
      await new Promise((resolve) => setTimeout(resolve, 200));

      const refreshed = await getFilesByMuestraId(muestra.ID_MUESTRA);
      setMuestraFiles(refreshed || []);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error("Error subiendo archivo:", err);
    } finally {
      if (isInputEvent) {
        eventOrFiles.target.value = "";
      }
    }
  };

  const persistFotosExport = useCallback(
    (config) => {
      if (!muestra?.ID_MUESTRA || !isEditMode) return;

      const serialized = config ? JSON.stringify(config) : null;
      setMuestra((prev) => ({ ...prev, FOTOS_EXPORT: serialized }));
      pendingFotosExportRef.current = config;

      if (fotosExportSaveRef.current) {
        clearTimeout(fotosExportSaveRef.current);
      }

      fotosExportSaveRef.current = setTimeout(async () => {
        fotosExportSaveRef.current = null;
        const toSave = pendingFotosExportRef.current;
        if (!toSave) return;
        pendingFotosExportRef.current = null;

        try {
          const saved = await patchMuestraFotosExport(
            muestra.ID_MUESTRA,
            toSave,
          );
          const fotosExportValue = saved.FOTOS_EXPORT ?? serialized;
          setMuestra((prev) => ({
            ...prev,
            FOTOS_EXPORT: fotosExportValue,
          }));
          onMuestraUpdated?.({
            ...muestra,
            ...saved,
            FOTOS_EXPORT: fotosExportValue,
          });
        } catch (err) {
          pendingFotosExportRef.current = toSave;
          console.error("Error guardando fotos PDF:", err);
        }
      }, 400);
    },
    [isEditMode, muestra, onMuestraUpdated],
  );

  const flushFotosExport = useCallback(async () => {
    if (fotosExportSaveRef.current) {
      clearTimeout(fotosExportSaveRef.current);
      fotosExportSaveRef.current = null;
    }

    const toSave = pendingFotosExportRef.current;
    if (!toSave || !muestra?.ID_MUESTRA || !isEditMode) return;

    pendingFotosExportRef.current = null;

    try {
      const saved = await patchMuestraFotosExport(muestra.ID_MUESTRA, toSave);
      const fotosExportValue = saved.FOTOS_EXPORT ?? JSON.stringify(toSave);
      setMuestra((prev) => ({
        ...prev,
        FOTOS_EXPORT: fotosExportValue,
      }));
      onMuestraUpdated?.({
        ...muestra,
        ...saved,
        FOTOS_EXPORT: fotosExportValue,
      });
    } catch (err) {
      pendingFotosExportRef.current = toSave;
      console.error("Error guardando fotos PDF al cerrar:", err);
    }
  }, [isEditMode, muestra, onMuestraUpdated]);

  const handleCloseDrawer = async () => {
    preparingPhotosRef.current = false;
    setPreparingPhotos(false);
    await flushFotosExport();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      preparingPhotosRef.current = false;
      setPreparingPhotos(false);
      setPreparePhotoProgress(null);
    }
  }, [open]);

  const fotosExportConfig = useMemo(
    () => parseFotosExport(muestra?.FOTOS_EXPORT),
    [muestra?.FOTOS_EXPORT],
  );

  // 🔷 Función applyEstadoTransition
  const applyEstadoTransition = async () => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    const nuevoEstado = pendingEstado;

    // 🔐 Hard lock si ya está en un estado de llegada
    if (isArrivedEstado(muestra.ID_ESTADO, tiposEstado)) {
      setConfirmDialogOpen(false);
      setPendingEstado(null);
      return;
    }

    let updatedState = { ...muestra };

    if (nuevoEstado === 1) {
      updatedState = {
        ...updatedState,
        ID_ESTADO: 1,
        FECHA_PETICION: null,
        FECHA_GET_SUPPLIER: null,
        FECHA_ENVIO: null,
        FECHA_RECEPCION: null,
      };
    }

    if (nuevoEstado === 2) {
      updatedState = {
        ...updatedState,
        ID_ESTADO: 2,
        FECHA_PETICION: today,
        FECHA_GET_SUPPLIER: null,
        FECHA_ENVIO: null,
        FECHA_RECEPCION: null,
      };
    }

    if (nuevoEstado === 3) {
      updatedState = {
        ...updatedState,
        ID_ESTADO: 3,
        FECHA_GET_SUPPLIER: today,
        FECHA_ENVIO: null,
        FECHA_RECEPCION: null,
      };
    }

    if (nuevoEstado === 5) {
      updatedState = {
        ...updatedState,
        ID_ESTADO: 5,
        FECHA_ENVIO: today,
        FECHA_RECEPCION: null,
      };
    }

    if (isArrivedEstado(nuevoEstado, tiposEstado)) {
      updatedState = {
        ...updatedState,
        ID_ESTADO: nuevoEstado,
        FECHA_RECEPCION: today,
      };
    }

    try {
      // 🔥 1️⃣ Persistir SIEMPRE antes de continuar
      const saved = await saveMuestra(updatedState);

      // 🔥 2️⃣ Actualizar estado en memoria con ID real (si no, el siguiente pedido crea otra muestra)
      const merged = {
        ...updatedState,
        ...(saved || {}),
        ID_MUESTRA:
          saved?.ID_MUESTRA ||
          updatedState.ID_MUESTRA ||
          muestra.ID_MUESTRA ||
          null,
        MUESTRA: saved?.MUESTRA || updatedState.MUESTRA || muestra.MUESTRA,
        QR_CODE: saved?.QR_CODE || updatedState.QR_CODE || muestra.QR_CODE,
      };
      setMuestra(merged);

      if (merged.ID_MUESTRA) {
        try {
          await importPendingMeetingMediaIfAny(merged.ID_MUESTRA);
        } catch (importErr) {
          console.error(
            "Error importing meeting media on status change:",
            importErr,
          );
        }
      }

      // 🔥 3️⃣ Si es ORDERED → abrir email dialog
      if (nuevoEstado === 2) {
        setAdditionalMessage((prev) => {
          const notes = String(merged.NOTAS || muestra.NOTAS || "").trim();
          return prev?.trim() ? prev : notes;
        });
        setOrderEmailDialog(true);
        loadOrderEmailAttachments(merged.ID_MUESTRA);
      }
    } catch (err) {
      console.error("Error saving status transition:", err);
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message;
      showErrorDialog(
        apiMsg || t("Samples.saveError"),
        t("Samples.shipmentErrorTitle"),
      );
    }

    setConfirmDialogOpen(false);
    setPendingEstado(null);
  };

  const openShipmentDialog = (mode) => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    if (mode === "sent") {
      setShipmentDialogMode("sent");
      setSentToClientForm({
        ...emptyEnvioDestino(),
        TIPO_DESTINO: DESTINO_INTERNO,
        FECHA_ENVIO: today,
        NUM_COURIER: "",
      });
      setShipmentDialogOpen(true);
      return;
    }

    if (mode === "sentToSupplier") {
      setShipmentDialogMode("sentToSupplier");
      setSentToClientForm({
        ...emptyEnvioDestino(),
        TIPO_DESTINO: DESTINO_PROVEEDOR,
        FECHA_ENVIO: today,
        NUM_COURIER: "",
      });
      setShipmentDialogOpen(true);
      return;
    }

    setShipmentDialogMode("sentToClient");
    setSentToClientForm({
      ...emptyEnvioDestino(),
      TIPO_DESTINO: DESTINO_CLIENTE,
      FECHA_ENVIO: today,
      NUM_COURIER: "",
    });
    setShipmentDialogOpen(true);
  };

  const clearShipmentMovimientos = async () => {
    const toDelete = (movimientos || []).filter(isShipmentMovimiento);
    for (const mov of toDelete) {
      if (mov.ID_MOVIMIENTO) {
        await deleteMovimiento(mov.ID_MOVIMIENTO);
      }
    }
    setMovimientos((prev) => prev.filter((m) => !isShipmentMovimiento(m)));
  };

  const handleConfirmShipmentDialog = async () => {
    if (!muestra.ID_MUESTRA) {
      showErrorDialog(t("Samples.saveBeforeSentToClient"));
      return;
    }

    const targetEstadoId =
      shipmentDialogMode === "sent"
        ? SENT_ESTADO_ID
        : shipmentDialogMode === "sentToSupplier"
          ? sentToSupplierEstadoId
          : sentToClientEstadoId;

    if (!targetEstadoId) {
      showErrorDialog(t("Samples.sentToClientShipmentError"));
      return;
    }

    if (
      !sentToClientForm.FECHA_ENVIO ||
      !isEnvioDestinoValid(sentToClientForm, {
        requireContact: shipmentDialogMode !== "sent",
      })
    ) {
      showErrorDialog(t("Samples.completeDestinationAndPerson"));
      return;
    }

    if (
      requiresCourierOnShipment({ isChinaUser: isReadOnly }) &&
      !String(sentToClientForm.NUM_COURIER || "").trim()
    ) {
      showErrorDialog(t("Samples.courierRequiredChina"));
      return;
    }

    setSavingSentToClient(true);

    try {
      const formatted = formatMuestraDateForAPI(sentToClientForm.FECHA_ENVIO);

      const fechasValidas = [
        ...movimientos.map((m) => m.FECHA_ENVIO).filter(Boolean),
        formatted,
      ].sort();
      const fechaEnvioMuestra = fechasValidas[0]
        ? parseMuestraDate(fechasValidas[0])
        : parseMuestraDate(sentToClientForm.FECHA_ENVIO);

      const destinoPayload = toMovimientoDestinoPayload(sentToClientForm);

      const savedMov = await addMovimiento({
        ID_MUESTRA: muestra.ID_MUESTRA,
        FECHA_ENVIO: formatted,
        NUM_COURIER: sentToClientForm.NUM_COURIER?.trim() || null,
        ...destinoPayload,
      });

      // Update only sample status/date — avoid full saveMuestra (escandallos/movimientos sync)
      // which often fails for proveedor users when ID_Marca/ID_PRODUCTO are incomplete in form state.
      const marcaId =
        muestra.ID_Marca ||
        (shipmentDialogMode === "sent" ? findSdsMarcaId(tiposMarcas) : null);
      const nombreCliente =
        muestra.NombreCliente ||
        muestra.NOMBRE_DISENO ||
        muestra.MARCA ||
        (shipmentDialogMode === "sent" ? "SDS" : null);

      await updateMuestra(muestra.ID_MUESTRA, {
        MUESTRA: muestra.MUESTRA,
        ID_TIPO_MUESTRA: muestra.ID_TIPO_MUESTRA,
        ID_ESTADO: targetEstadoId,
        ID_TALLA_BASE: muestra.ID_TALLA_BASE,
        ID_COLOR_BASE: muestra.ID_COLOR_BASE,
        PROVEEDOR: muestra.PROVEEDOR,
        ID_Marca: marcaId,
        ID_RESPONSABLE_SUPPLIER: muestra.ID_RESPONSABLE_SUPPLIER,
        ID_PRODUCTO: muestra.ID_PRODUCTO,
        NombreCliente: nombreCliente,
        FECHA_PETICION: formatMuestraDateForAPI(muestra.FECHA_PETICION),
        FECHA_PREVISTA: formatMuestraDateForAPI(muestra.FECHA_PREVISTA),
        FECHA_GET_SUPPLIER: formatMuestraDateForAPI(muestra.FECHA_GET_SUPPLIER),
        FECHA_ENVIO: formatMuestraDateForAPI(fechaEnvioMuestra),
        FECHA_RECEPCION: formatMuestraDateForAPI(muestra.FECHA_RECEPCION),
        FECHA_PREVISTA_FABRICA: formatMuestraDateForAPI(
          muestra.FECHA_PREVISTA_FABRICA,
        ),
        PRIORIDAD: muestra.PRIORIDAD || null,
        DEVELOPMENT: muestra.DEVELOPMENT || null,
        CALIDAD_NOMBRE: muestra.CALIDAD_NOMBRE || null,
        CALIDAD_REFERENCIA: muestra.CALIDAD_REFERENCIA || null,
        CALIDAD_MATERIAL: muestra.CALIDAD_MATERIAL || null,
        CALIDAD_ESTADO: muestra.CALIDAD_ESTADO || null,
        PRINT_NOMBRE: muestra.PRINT_NOMBRE || null,
        PRINT_ESTADO: muestra.PRINT_ESTADO || null,
        NOTAS: muestra.NOTAS,
      });

      const updatedState = {
        ...muestra,
        ID_ESTADO: targetEstadoId,
        FECHA_ENVIO: fechaEnvioMuestra,
        ID_Marca: marcaId || muestra.ID_Marca,
        NombreCliente: nombreCliente || muestra.NombreCliente,
      };

      setMuestra(updatedState);
      setMovimientos((prev) => [...prev, savedMov]);
      setShipmentDialogOpen(false);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error("Error marking shipment status:", err);
      const apiMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message;
      showErrorDialog(
        (shipmentDialogMode === "sent"
          ? t("Samples.sentShipmentError")
          : t("Samples.sentToClientShipmentError")) +
          (apiMsg ? `\n\n${apiMsg}` : ""),
        t("Samples.shipmentErrorTitle"),
      );
    } finally {
      setSavingSentToClient(false);
    }
  };

  // Reset save lock when drawer opens/closes
  useEffect(() => {
    if (open) {
      savingRef.current = false;
      setSaving(false);
    }
  }, [open]);

  const importPendingMeetingMediaIfAny = useCallback(
    async (muestraId) => {
      const payload = pendingMeetingMediaRef.current || pendingMeetingMedia;
      if (
        !muestraId ||
        !payload?.sessionId ||
        !Array.isArray(payload.photoIds) ||
        !payload.photoIds.length
      ) {
        return null;
      }
      const result = await importMeetingMediaToMuestra(muestraId, {
        sessionId: payload.sessionId,
        photoIds: payload.photoIds,
      });
      clearCachedArchivos(muestraId);
      // Invalidar cargas concurrentes (race con useEffect al asignar ID_MUESTRA)
      const gen = ++filesLoadGenRef.current;
      const refreshed = await getFilesByMuestraId(muestraId);
      if (gen === filesLoadGenRef.current) {
        setMuestraFiles(refreshed || []);
        setLoadingFiles(false);
      }
      pendingMeetingMediaRef.current = null;
      onMeetingMediaImported?.(result);
      return result;
    },
    [onMeetingMediaImported, pendingMeetingMedia],
  );

  const persistMuestraAndImport = useCallback(async () => {
    await flushFotosExport();
    const saved = await saveMuestra();
    if (!saved?.ID_MUESTRA) {
      throw new Error(t("Samples.saveError"));
    }

    // Asegurar QR en estado (upload de fotos lo necesita)
    let withQr = saved;
    if (!saved.QR_CODE) {
      try {
        withQr = await getMuestraById(saved.ID_MUESTRA);
        setMuestra(withQr);
      } catch {
        /* keep saved */
      }
    } else {
      setMuestra((prev) => ({ ...prev, ...saved }));
    }

    try {
      await importPendingMeetingMediaIfAny(saved.ID_MUESTRA);
    } catch (importErr) {
      console.error("Error importing meeting media:", importErr);
      alert(t("Meetings.importMeetingMediaError"));
    }

    if (handleAddMuestraLocally) {
      handleAddMuestraLocally(saved.ID_PRODUCTO, saved);
    }
    if (onMuestraUpdated) {
      onMuestraUpdated(saved);
    }
    if (typeof onMuestraPersisted === "function") {
      onMuestraPersisted(withQr || saved);
    }
    return withQr || saved;
  }, [
    flushFotosExport,
    saveMuestra,
    importPendingMeetingMediaIfAny,
    handleAddMuestraLocally,
    onMuestraUpdated,
    onMuestraPersisted,
    setMuestra,
    t,
  ]);

  // Cargar archivos cuando se abre el Drawer
  useEffect(() => {
    if (!open) return;

    const id = muestra?.ID_MUESTRA;
    if (!id) {
      setMuestraFiles([]);
      return;
    }

    const gen = ++filesLoadGenRef.current;
    setLoadingFiles(true);
    let cancelled = false;

    const loadFiles = async () => {
      try {
        const files = await getFilesByMuestraId(id);
        // No pisar un import más reciente ni respuestas obsoletas
        if (!cancelled && gen === filesLoadGenRef.current) {
          setMuestraFiles(files || []);
        }
      } catch (err) {
        if (!cancelled) console.error("Error cargando archivos muestra:", err);
      } finally {
        if (!cancelled && gen === filesLoadGenRef.current) {
          setLoadingFiles(false);
        }
      }
    };

    loadFiles();
    return () => {
      cancelled = true;
    };
  }, [open, muestra?.ID_MUESTRA]);

  const generalMuestraFiles = useMemo(
    () => (muestraFiles || []).filter((file) => !file.category),
    [muestraFiles],
  );
  const displayMuestraFiles = useMemo(
    () =>
      orderMuestraMediaForDisplay(generalMuestraFiles, muestra?.FOTOS_EXPORT),
    [generalMuestraFiles, muestra?.FOTOS_EXPORT],
  );
  const calidadesFiles = useMemo(
    () => (muestraFiles || []).filter((file) => file.category === "Calidades"),
    [muestraFiles],
  );
  const printFiles = useMemo(
    () => (muestraFiles || []).filter((file) => file.category === "Print"),
    [muestraFiles],
  );

  const handleReorderPhotos = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;
      const files = [...displayMuestraFiles];
      const [moved] = files.splice(fromIndex, 1);
      files.splice(toIndex, 0, moved);
      const config = buildFotosExportConfig(
        files,
        fotosExportConfig?.front,
        fotosExportConfig?.back,
      );
      persistFotosExport(config);
    },
    [displayMuestraFiles, fotosExportConfig, persistFotosExport],
  );

  const handleSetPhotoRole = useCallback(
    (file, role) => {
      const key = getMuestraFileKey(file);
      let front = fotosExportConfig?.front || null;
      let back = fotosExportConfig?.back || null;

      if (role === "front") {
        front = front === key ? null : key;
        if (back === key) back = null;
      } else if (role === "back") {
        back = back === key ? null : key;
        if (front === key) front = null;
      }

      const config = buildFotosExportConfig(displayMuestraFiles, front, back);
      persistFotosExport(config);
    },
    [displayMuestraFiles, fotosExportConfig, persistFotosExport],
  );

  const handlePreparePhotos = useCallback(
    async (engine = "garment") => {
      if (!muestra?.ID_MUESTRA || preparingPhotosRef.current) return;

      await flushFotosExport();

      const generalFiles = (muestraFiles || []).filter(
        (file) => !file.category,
      );
      const { front, back } = pickFrontAndBackFromExport(
        generalFiles,
        muestra.FOTOS_EXPORT,
      );
      const frontSource = resolveOriginalMuestraPhotoForPrepare(
        generalFiles,
        front,
      );
      const backSource = resolveOriginalMuestraPhotoForPrepare(
        generalFiles,
        back,
      );

      const jobs = [];
      if (frontSource) {
        jobs.push({
          path: getMuestraFileKey(frontSource),
          role: "front",
        });
      }
      if (backSource) {
        const backPath = getMuestraFileKey(backSource);
        if (!jobs.some((job) => job.path === backPath)) {
          jobs.push({ path: backPath, role: "back" });
        }
      }

      if (!jobs.length) {
        window.alert(t("Samples.preparePhotosNoSelection"));
        return;
      }

      preparingPhotosRef.current = true;
      setPreparingPhotos(true);
      const failures = [];
      const previewItems = [];
      const engineFallbacks = [];
      let lastMuestra = muestra;

      try {
        for (let i = 0; i < jobs.length; i += 1) {
          const job = jobs[i];
          setPreparePhotoProgress({ current: i + 1, total: jobs.length });

          try {
            const result = await prepareMuestraPhoto(muestra.ID_MUESTRA, {
              path: job.path,
              role: job.role,
              engine,
            });
            if (result?.ok && result.output) {
              previewItems.push({
                path: result.output,
                role: result.role || job.role,
                engine: result.engine,
              });
              if (engine === "garment" && result.engine === "rembg") {
                engineFallbacks.push(job.role || job.path);
              }
            }
            if (result?.fotosExport) {
              const serialized = JSON.stringify(result.fotosExport);
              lastMuestra = {
                ...(result.muestra || lastMuestra),
                FOTOS_EXPORT: serialized,
              };
              setMuestra((prev) => ({
                ...prev,
                FOTOS_EXPORT: serialized,
              }));
            } else if (result?.muestra) {
              lastMuestra = result.muestra;
              setMuestra((prev) => ({ ...prev, ...result.muestra }));
            }
          } catch (err) {
            failures.push({
              path: job.path,
              error:
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                err.message,
            });
          }
        }

        if (lastMuestra) {
          onMuestraUpdated?.(lastMuestra);
        }

        clearCachedArchivos(muestra.ID_MUESTRA);
        const refreshed = await getFilesByMuestraId(muestra.ID_MUESTRA);
        setMuestraFiles(refreshed || []);

        if (failures.length) {
          window.alert(
            `${t("Samples.preparePhotosPartial")}\n${failures
              .map((f) => f.error)
              .filter(Boolean)
              .join("\n")}`,
          );
        } else if (previewItems.length) {
          if (engineFallbacks.length) {
            window.alert(t("Samples.preparePhotosGarmentFallback"));
          }
          setCleanPhotosPreview({ open: true, items: previewItems });
        }
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err.message ||
          t("Samples.preparePhotosError");
        window.alert(msg);
      } finally {
        preparingPhotosRef.current = false;
        setPreparingPhotos(false);
        setPreparePhotoProgress(null);
      }
    },
    [muestra, muestraFiles, t, onMuestraUpdated, setMuestra, flushFotosExport],
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseDrawer}
      PaperProps={{
        className: isReadOnly ? "drawer-readonly" : "",
        sx: {
          borderLeft: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 36%)`,
        },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 750 },
          maxWidth: "100vw",
          height: "100vh",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* CONTENIDO OCULTO A IMPRIMIR */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <PrintEtiqueta ref={etiquetaRef} muestra={muestra} />
        </div>

        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <HeaderSection
              muestra={muestra}
              pendingReception={isShippedPendingReceptionEstado(
                muestra?.ID_ESTADO,
                tiposEstado,
              )}
              arrived={isArrivedEstado(muestra?.ID_ESTADO, tiposEstado)}
              isEditMode={!isReadOnly && effectiveEditMode}
              canDeletePhotos={Boolean(muestra?.ID_MUESTRA)}
              confirmDelete={(id) => openDeleteDialog(id)}
              onPrintEtiqueta={handlePrintEtiqueta}
              muestraFiles={displayMuestraFiles}
              fotosExport={fotosExportConfig}
              onReorderPhotos={handleReorderPhotos}
              onSetPhotoRole={handleSetPhotoRole}
              onDeleteFile={handleRequestDeleteFile}
              onUploadFile={(e) => handleUploadMuestraFile(e)}
              onPreparePhotos={handlePreparePhotos}
              preparingPhotos={preparingPhotos}
              preparePhotoProgress={preparePhotoProgress}
              onPreviewCleanPhotos={(items) =>
                setCleanPhotosPreview({ open: true, items })
              }
            />

            <PreparePhotosPreviewDialog
              open={cleanPhotosPreview.open}
              items={cleanPhotosPreview.items}
              onClose={() => setCleanPhotosPreview({ open: false, items: [] })}
            />

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: { xs: 1.25, sm: 1.75 },
                py: 1.25,
                minHeight: 0,
              }}
            >
              <GeneralSection
                {...{
                  muestra,
                  setMuestra: (updateFn) => {
                    setMuestra((prev) => {
                      const updated =
                        typeof updateFn === "function"
                          ? updateFn(prev)
                          : updateFn;

                      // Llegada (ARRIVED / Arrived to Client / Supplier): bloqueo salvo Color
                      if (isArrivedEstado(prev.ID_ESTADO, tiposEstado)) {
                        return applyGeneralEditForArrived(
                          prev,
                          updated,
                          tiposEstado,
                        );
                      }

                      const estadoActual = prev.ID_ESTADO;
                      const nuevoEstado = updated.ID_ESTADO;

                      // =========================
                      // 🏭 CHINA (Factory)
                      // =========================
                      if (isReadOnly) {
                        const goingToSentToClient =
                          estadoActual !== nuevoEstado &&
                          isSentToClientEstado(nuevoEstado, tiposEstado);
                        const goingToSentToSupplier =
                          estadoActual !== nuevoEstado &&
                          isSentToSupplierEstado(nuevoEstado, tiposEstado);
                        const goingReception =
                          estadoActual !== nuevoEstado &&
                          isReceptionTransition(
                            estadoActual,
                            nuevoEstado,
                            tiposEstado,
                          );

                        // 🚫 Enviado: recepción cierra el envío; otro cambio solo admin (anula).
                        // Desde SENT (China→SDS) sí puede pasar a Sent to Client / Supplier.
                        if (
                          isShippedPendingReceptionEstado(
                            estadoActual,
                            tiposEstado,
                          ) &&
                          estadoActual !== nuevoEstado
                        ) {
                          if (goingReception) {
                            setPendingEstado(nuevoEstado);
                            setConfirmDialogOpen(true);
                            return prev;
                          }
                          const allowedFromSent =
                            Number(estadoActual) === SENT_ESTADO_ID &&
                            (goingToSentToClient || goingToSentToSupplier);
                          if (!allowedFromSent) {
                            if (isAdmin) {
                              setPendingRevertEstado(nuevoEstado);
                              setConfirmRevertShipmentOpen(true);
                            }
                            return {
                              ...prev,
                              ID_RESPONSABLE_SUPPLIER:
                                updated.ID_RESPONSABLE_SUPPLIER,
                            };
                          }
                        }

                        // 🔔 Cambio a OK FACTORY
                        if (estadoActual !== 3 && nuevoEstado === 3) {
                          setConfirmOkFactoryDialog(true);
                          return prev;
                        }

                        // 🔔 Cambio a SENT (solo si ya estaba en OK FACTORY)
                        if (estadoActual === 3 && nuevoEstado === 5) {
                          openShipmentDialog("sent");
                          return prev;
                        }

                        // 🔔 Cambio a SENT TO CLIENT (+ shipment)
                        if (goingToSentToClient) {
                          openShipmentDialog("sentToClient");
                          return prev;
                        }

                        // 🔔 Cambio a SENT TO SUPPLIER (+ shipment)
                        if (goingToSentToSupplier) {
                          openShipmentDialog("sentToSupplier");
                          return prev;
                        }

                        // Bloquear otros cambios de estado; permitir responsable proveedor
                        if (estadoActual !== nuevoEstado) {
                          return prev;
                        }

                        return {
                          ...prev,
                          ID_RESPONSABLE_SUPPLIER:
                            updated.ID_RESPONSABLE_SUPPLIER,
                        };
                      }

                      // =========================
                      // 🔁 MAQUINA DE ESTADOS CON CONFIRMACION
                      // =========================

                      if (
                        isShippedPendingReceptionEstado(
                          estadoActual,
                          tiposEstado,
                        ) &&
                        estadoActual !== nuevoEstado
                      ) {
                        // Recepcion: cierra el envio (no borra)
                        if (
                          isReceptionTransition(
                            estadoActual,
                            nuevoEstado,
                            tiposEstado,
                          )
                        ) {
                          if (!prev.ID_MUESTRA) {
                            queueMicrotask(() =>
                              showErrorDialog(
                                t("Samples.saveBeforeStatusChange"),
                                t("Samples.saveBeforeStatusChangeTitle"),
                              ),
                            );
                            return prev;
                          }
                          setPendingEstado(nuevoEstado);
                          setConfirmDialogOpen(true);
                          return prev;
                        }

                        // Anular: solo admin
                        if (isAdmin) {
                          setPendingRevertEstado(nuevoEstado);
                          setConfirmRevertShipmentOpen(true);
                        } else {
                          queueMicrotask(() =>
                            showErrorDialog(
                              t("Samples.shipmentStatusLockedBody"),
                              t("Samples.shipmentStatusLockedTitle"),
                            ),
                          );
                        }
                        return prev;
                      }

                      if (
                        estadoActual !== nuevoEstado &&
                        isSentToSupplierEstado(nuevoEstado, tiposEstado)
                      ) {
                        openShipmentDialog("sentToSupplier");
                        return prev;
                      }

                      if (
                        estadoActual !== nuevoEstado &&
                        isSentToClientEstado(nuevoEstado, tiposEstado)
                      ) {
                        openShipmentDialog("sentToClient");
                        return prev;
                      }

                      if (estadoActual !== nuevoEstado) {
                        if (!prev.ID_MUESTRA) {
                          // Evita crear N muestras al pedir sin haber guardado antes
                          queueMicrotask(() =>
                            showErrorDialog(
                              t("Samples.saveBeforeStatusChange"),
                              t("Samples.saveBeforeStatusChangeTitle"),
                            ),
                          );
                          return prev;
                        }
                        setPendingEstado(nuevoEstado);
                        setConfirmDialogOpen(true);
                        return prev; // NO aplicar aún
                      }

                      return updated;
                    });
                  },
                  tiposEstado,
                  tiposMuestra,
                  tiposTallas,
                  tiposColores,
                  tiposProveedores: sortedTiposProveedores,
                  tiposMarcas,
                  tiposResponsables,
                  shipmentStatusLocked: shipmentLocked,
                  onResponsableCreated: (created) => {
                    setTiposResponsables((prev) => {
                      if (prev.some((entry) => entry.ID === created.ID)) {
                        return prev;
                      }
                      return [...prev, created].sort((a, b) =>
                        a.Nombre.localeCompare(b.Nombre, undefined, {
                          sensitivity: "base",
                        }),
                      );
                    });
                  },
                  isEditMode,
                  isReadOnly,
                  onTipoMuestraChange: handleTipoMuestraChange,
                }}
              />

              <DatesSection
                muestra={muestra}
                canEditGetSupplier={canEditGetSupplier}
                setMuestra={(updateFn) => {
                  setMuestra((prev) => {
                    const updated =
                      typeof updateFn === "function"
                        ? updateFn(prev)
                        : updateFn;

                    if (isAdmin) return updated;

                    if (isReadOnly) {
                      return {
                        ...prev,
                        FECHA_GET_SUPPLIER: updated.FECHA_GET_SUPPLIER,
                        FECHA_PREVISTA_FABRICA: updated.FECHA_PREVISTA_FABRICA,
                      };
                    }

                    return {
                      ...updated,
                      FECHA_GET_SUPPLIER: prev.FECHA_GET_SUPPLIER,
                    };
                  });
                }}
              />

              <div className="calidades-section">
                <CalidadesPrintSection
                  muestra={muestra}
                  setMuestra={setMuestra}
                  isReadOnly={false}
                  selectedProduct={selectedProduct}
                  calidadesFiles={calidadesFiles}
                  printFiles={printFiles}
                  onUploadCalidades={(e) =>
                    handleUploadMuestraFile(e, "Calidades")
                  }
                  onUploadPrint={(e) => handleUploadMuestraFile(e, "Print")}
                  onDeleteCalidades={handleRequestDeleteFile}
                  onDeletePrint={handleRequestDeleteFile}
                />
              </div>

              <div className="escandallos-section">
                <EscandallosSection
                  {...{
                    escandallos,
                    setEscandallos,
                    escandalloGrupos,
                    tiposProveedores: sortedTiposProveedores,
                    getSubgrupos: loadSubgrupos,
                    deleteEscandallo,
                  }}
                />
              </div>
              <MovimientosSection
                {...{
                  movimientos,
                  /* setMovimientos: isReadOnly ? () => {} : setMovimientos, esto si lo activo bloquea China */
                  setMovimientos,
                  tiposMarcas,
                  muestra,
                  setMuestra,
                  deleteMovimiento,
                  sentToClientEstadoId,
                  sentToSupplierEstadoId,
                  tiposEstado,
                }}
              />
              <CourierControlMuestraLinks muestra={muestra} />
            </Box>

            <SaveBar
              userCanCreateSample={userCanCreateSample}
              muestra={muestra}
              saving={saving}
              keepOpenAfterSave={keepOpenAfterSave}
              onCancel={handleCloseDrawer}
              onCreateSample={async () => {
                if (savingRef.current) return;
                savingRef.current = true;
                setSaving(true);
                try {
                  const saved = await persistMuestraAndImport();
                  if (keepOpenAfterSave) {
                    savingRef.current = false;
                    setSaving(false);
                    return;
                  }
                  await handleCloseDrawer();
                  if (onSaveMuestra) {
                    void Promise.resolve(
                      onSaveMuestra(saved.ID_MUESTRA, saved),
                    );
                  }
                  if (onRefresh) {
                    void onRefresh();
                  }
                } catch (err) {
                  console.error(
                    "❌ Error creating sample in Critical Path:",
                    err,
                  );
                  alert(t("Samples.criticalPathCreateError"));
                  savingRef.current = false;
                  setSaving(false);
                }
              }}
              onSaveAndClose={async () => {
                if (savingRef.current) return;
                savingRef.current = true;
                setSaving(true);
                try {
                  const saved = await persistMuestraAndImport();
                  if (keepOpenAfterSave) {
                    savingRef.current = false;
                    setSaving(false);
                    return;
                  }
                  await handleCloseDrawer();
                  if (onSaveMuestra) {
                    void Promise.resolve(
                      onSaveMuestra(saved.ID_MUESTRA, saved),
                    );
                  }
                  if (onRefresh) {
                    void onRefresh();
                  }
                } catch (err) {
                  console.error("❌ Error saving muestra:", err);
                  const apiMsg =
                    err?.response?.data?.error ||
                    err?.response?.data?.detail ||
                    err?.message;
                  alert(
                    `${t("Samples.saveError")}${apiMsg ? `\n\n${apiMsg}` : ""}`,
                  );
                  savingRef.current = false;
                  setSaving(false);
                }
              }}
            />
          </>
        )}
      </Box>

      {/* DIALOGO CONFIRMAR ELIMINACIÓN */}
      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
        <DialogTitle>{t("Samples.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Samples.confirmDeleteBody", { name: muestra?.MUESTRA || "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>{t("Samples.cancel")}</Button>
          <Button
            onClick={handleDeleteMuestra}
            color="error"
            variant="contained"
          >
            {t("Samples.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={errorDialog.open} onClose={closeErrorDialog}>
        <DialogTitle>
          {errorDialog.title || t("Samples.errorTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {errorDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeErrorDialog} autoFocus>
            {t("Samples.accept")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG ELIMINAR FOTOS DESDE EL DRAWER MUESTRA */}
      <Dialog
        open={deleteFileDialogOpen}
        onClose={() => setDeleteFileDialogOpen(false)}
      >
        <DialogTitle>{t("Samples.confirmFileDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Samples.confirmFileDeleteBody", {
              name: fileToDelete?.name || "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteFileDialogOpen(false);
              setFileToDelete(null);
            }}
          >
            {t("Samples.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeleteFile}
            variant="contained"
            color="error"
          >
            {t("Samples.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOGO CONFIRMAR CAMBIO DE ESTADO A OK FACTORY */}

      <Dialog
        open={confirmOkFactoryDialog}
        onClose={() => {
          setConfirmOkFactoryDialog(false);
          setPendingStatusChange(null);
        }}
      >
        <DialogTitle>{t("Samples.confirmOkFactoryTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Samples.confirmOkFactoryBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOkFactoryDialog(false);
              setPendingStatusChange(null);
            }}
          >
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const today = new Date();
              today.setHours(12, 0, 0, 0);

              const updatedState = {
                ...muestra,
                ID_ESTADO: 3,
                FECHA_GET_SUPPLIER: today,
              };

              await saveMuestra(updatedState);
              setMuestra(updatedState);

              setConfirmOkFactoryDialog(false);
              setPendingStatusChange(null);
            }}
          >
            {t("Samples.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOGO SENT / SENT TO CLIENT + SHIPMENT */}
      <Dialog
        open={shipmentDialogOpen}
        onClose={() => {
          if (savingSentToClient) return;
          setShipmentDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {shipmentDialogMode === "sent"
            ? t("Samples.sentToSdsTitle")
            : shipmentDialogMode === "sentToSupplier"
              ? t("Samples.sentToSupplierTitle")
              : t("Samples.sentToClientTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {shipmentDialogMode === "sent"
              ? t("Samples.sentToSdsBody")
              : shipmentDialogMode === "sentToSupplier"
                ? t("Samples.sentToSupplierBody")
                : t("Samples.sentToClientBody")}
            {requiresCourierOnShipment({ isChinaUser: isReadOnly })
              ? ` ${t("Samples.courierRequiredChinaHint")}`
              : ""}
          </DialogContentText>

          <Box sx={{ mt: 1 }}>
            <EnvioDestinoFields
              value={sentToClientForm}
              onChange={(next) =>
                setSentToClientForm((prev) => ({
                  ...prev,
                  ...next,
                }))
              }
              disabled={savingSentToClient}
              allowedTipos={
                shipmentDialogMode === "sent"
                  ? [DESTINO_INTERNO]
                  : shipmentDialogMode === "sentToSupplier"
                    ? [DESTINO_PROVEEDOR]
                    : [DESTINO_CLIENTE]
              }
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <DatePicker
              selected={
                sentToClientForm.FECHA_ENVIO
                  ? new Date(sentToClientForm.FECHA_ENVIO)
                  : null
              }
              locale={es}
              dateFormat="dd/MM/yyyy"
              portalId="muestra-drawer-datepicker-portal"
              popperClassName="muestra-drawer-datepicker-popper"
              popperProps={{ strategy: "fixed" }}
              onChange={(date) => {
                if (!date) {
                  setSentToClientForm((prev) => ({
                    ...prev,
                    FECHA_ENVIO: null,
                  }));
                  return;
                }

                const safe = new Date(date);
                safe.setHours(12, 0, 0, 0);
                setSentToClientForm((prev) => ({
                  ...prev,
                  FECHA_ENVIO: safe,
                }));
              }}
              customInput={
                <TextField fullWidth label={t("Samples.sendDate")} />
              }
            />
          </Box>

          <TextField
            fullWidth
            margin="normal"
            required={requiresCourierOnShipment({ isChinaUser: isReadOnly })}
            label={t("Samples.courierNumber")}
            value={sentToClientForm.NUM_COURIER || ""}
            onChange={(e) =>
              setSentToClientForm((prev) => ({
                ...prev,
                NUM_COURIER: e.target.value,
              }))
            }
            helperText={
              requiresCourierOnShipment({ isChinaUser: isReadOnly })
                ? t("Samples.courierRequiredChinaHint")
                : t("Samples.courierOptionalSitgesHint")
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShipmentDialogOpen(false)}
            disabled={savingSentToClient}
          >
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmShipmentDialog}
            disabled={savingSentToClient}
          >
            {savingSentToClient ? t("Samples.saving") : t("Samples.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmRevertShipmentOpen}
        onClose={() => {
          if (revertingShipment) return;
          setConfirmRevertShipmentOpen(false);
          setPendingRevertEstado(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Samples.revertShipmentStatusTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("Samples.revertShipmentStatusBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmRevertShipmentOpen(false);
              setPendingRevertEstado(null);
            }}
            disabled={revertingShipment}
          >
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={revertingShipment}
            onClick={async () => {
              if (pendingRevertEstado == null) return;
              setRevertingShipment(true);
              try {
                await clearShipmentMovimientos();
                setPendingEstado(pendingRevertEstado);
                setConfirmRevertShipmentOpen(false);
                setPendingRevertEstado(null);
                setConfirmDialogOpen(true);
              } catch (err) {
                console.error("Error reverting shipment status:", err);
                showErrorDialog(
                  err?.response?.data?.error || t("Samples.saveError"),
                  t("Samples.shipmentErrorTitle"),
                );
              } finally {
                setRevertingShipment(false);
              }
            }}
          >
            {revertingShipment ? t("Samples.saving") : t("Samples.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOGO CONFIRMAR CAMBIO DE ESTADO A OK FACTORY */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>{t("Samples.confirmStatusChangeTitle")}</DialogTitle>

        <DialogContent>
          <Typography>{t("Samples.confirmStatusChangeBody")}</Typography>

          {pendingEstado === 2 && (
            <Typography
              sx={{ mt: 2, fontWeight: "bold", color: "warning.main" }}
            >
              {t("Samples.orderedEmailWarning")}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            {t("Samples.cancel")}
          </Button>

          <Button variant="contained" onClick={applyEstadoTransition}>
            {t("Samples.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/*  envio de emial a proveedor */}
      <Dialog
        open={orderEmailDialog}
        disableEnforceFocus
        maxWidth="md"
        fullWidth
        onClose={() => {
          if (sendingOrderEmail) return;
          setOrderEmailDialog(false);
        }}
      >
        <DialogTitle>{t("Samples.sendOrderEmailTitle")}</DialogTitle>

        <DialogContent>
          <Autocomplete
            multiple
            freeSolo
            fullWidth
            disableCloseOnSelect
            options={sortedOrderEmailContacts}
            value={toRecipientValue}
            isOptionEqualToValue={recipientOptionEqual}
            getOptionLabel={getRecipientOptionLabel}
            onChange={handleRecipientChange("to")}
            componentsProps={{
              popper: {
                disablePortal: true,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Samples.selectRecipientsTo")}
                margin="normal"
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              if (typeof option === "string") {
                return (
                  <li key={key} {...optionProps}>
                    {option}
                  </li>
                );
              }
              return (
                <li key={key} {...optionProps}>
                  {option.email} - {option.group}
                </li>
              );
            }}
          />
          <TextField
            fullWidth
            label={t("Samples.emailTo")}
            margin="normal"
            value={orderEmailData.to}
            onChange={(e) =>
              setOrderEmailData({ ...orderEmailData, to: e.target.value })
            }
          />
          <Autocomplete
            multiple
            freeSolo
            fullWidth
            disableCloseOnSelect
            options={sortedOrderEmailContacts}
            value={ccRecipientValue}
            isOptionEqualToValue={recipientOptionEqual}
            getOptionLabel={getRecipientOptionLabel}
            onChange={handleRecipientChange("cc")}
            componentsProps={{
              popper: {
                disablePortal: true,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Samples.selectRecipientsCc")}
                margin="normal"
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              if (typeof option === "string") {
                return (
                  <li key={key} {...optionProps}>
                    {option}
                  </li>
                );
              }
              return (
                <li key={key} {...optionProps}>
                  {option.email} - {option.group}
                </li>
              );
            }}
          />

          <TextField
            fullWidth
            label={t("Samples.emailCc")}
            margin="normal"
            value={orderEmailData.cc}
            onChange={(e) =>
              setOrderEmailData({ ...orderEmailData, cc: e.target.value })
            }
          />
          <TextField
            label={t("Samples.additionalMessage")}
            multiline
            rows={4}
            fullWidth
            value={additionalMessage}
            onChange={(e) => setAdditionalMessage(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t("Samples.orderAttachmentsTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              effectiveOrderAttachMode === "fichaPdf"
                ? "Samples.orderAttachmentsHintFicha"
                : "Samples.orderAttachmentsHintLoose",
            )}
          </Typography>

          {loadingOrderAttachments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {effectiveOrderAttachMode === "fichaPdf" ? (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("Samples.orderFichaPdfNote")}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("Samples.orderLooseFilesNote")}
                </Typography>
              )}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  {t(
                    effectiveOrderAttachMode === "fichaPdf"
                      ? "Samples.orderSampleFilesAlways"
                      : "Samples.orderSampleFilesAlwaysLoose",
                    {
                      count: orderSampleFiles.length,
                    },
                  )}
                </Typography>
                {orderSampleFiles.length > 0 ? (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={1}
                    sx={{ gap: 1 }}
                  >
                    {orderSampleFiles.map((file) => (
                      <Box
                        key={`sample-${file.path || file.name}`}
                        sx={{
                          width: 88,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "background.paper",
                            "& > div": {
                              width: "100%",
                              justifyContent: "center",
                            },
                            "& img": {
                              width: "100% !important",
                              maxWidth: "100% !important",
                              height: "100%",
                              objectFit: "cover",
                            },
                          }}
                        >
                          {file.path ? (
                            <FileThumbnails filePath={file.path} />
                          ) : null}
                        </Box>
                        <Typography
                          variant="caption"
                          noWrap
                          title={file.name}
                          sx={{ width: "100%", textAlign: "center" }}
                        >
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t("Samples.orderSampleFilesEmpty")}
                  </Typography>
                )}
              </Box>

              {orderSampleVideos.length > 0 ? (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.75 }}
                  >
                    {t("Samples.orderSampleVideosAsLink", {
                      count: orderSampleVideos.length,
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("Samples.orderSampleVideosAsLinkHint")}
                  </Typography>
                  <Stack spacing={0.5}>
                    {orderSampleVideos.map((file) => (
                      <Typography
                        key={`video-${file.path || file.name}`}
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        ▶ {file.name}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 0.75 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("Samples.orderTechnicalFilesTitle", {
                      selected: selectedTechnicalKeys.length,
                      total: orderTechnicalFiles.length,
                    })}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      size="small"
                      onClick={selectAllTechnicalAttachments}
                      disabled={
                        !orderTechnicalFiles.length || sendingOrderEmail
                      }
                    >
                      {t("Samples.selectAllTechnical")}
                    </Button>
                    <Button
                      size="small"
                      onClick={clearTechnicalAttachments}
                      disabled={
                        !selectedTechnicalKeys.length || sendingOrderEmail
                      }
                    >
                      {t("Samples.clearTechnical")}
                    </Button>
                  </Stack>
                </Stack>

                {orderTechnicalFiles.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("Samples.orderTechnicalFilesEmpty")}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: 320,
                      overflowY: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Stack spacing={1}>
                      {orderTechnicalFiles.map((file) => {
                        const checked = selectedTechnicalKeys.includes(
                          file.key,
                        );
                        return (
                          <Box
                            key={file.key}
                            onClick={() =>
                              !sendingOrderEmail &&
                              toggleTechnicalAttachment(file.key)
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 0.75,
                              py: 0.5,
                              borderRadius: 1,
                              cursor: sendingOrderEmail ? "default" : "pointer",
                              bgcolor: checked
                                ? alpha(theme.palette.primary.main, 0.06)
                                : "transparent",
                              border: "1px solid",
                              borderColor: checked
                                ? alpha(theme.palette.primary.main, 0.35)
                                : "transparent",
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                              },
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={sendingOrderEmail}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() =>
                                toggleTechnicalAttachment(file.key)
                              }
                            />
                            <Box
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              sx={{
                                width: 64,
                                height: 64,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                                bgcolor: "background.paper",
                                "& > div": {
                                  width: "100%",
                                  justifyContent: "center",
                                },
                                "& img": {
                                  width: "100% !important",
                                  maxWidth: "100% !important",
                                  height: "100%",
                                  objectFit: "cover",
                                },
                              }}
                            >
                              {file.path ? (
                                <FileThumbnails filePath={file.path} />
                              ) : null}
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {file.folder}
                              </Typography>
                              <Typography
                                variant="body2"
                                noWrap
                                title={file.name}
                                sx={{ fontWeight: 600 }}
                              >
                                {file.name}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOrderEmailDialog(false)}
            disabled={sendingOrderEmail}
          >
            {t("Samples.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={handleSendOrderEmail}
            disabled={sendingOrderEmail || loadingOrderAttachments}
          >
            {sendingOrderEmail ? t("Samples.sending") : t("Samples.send")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveUnknownDialogOpen}
        onClose={() => {
          if (sendingOrderEmail) return;
          setSaveUnknownDialogOpen(false);
        }}
      >
        <DialogTitle>{t("Samples.saveNewRecipientsTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Samples.saveNewRecipientsBody")}
          </DialogContentText>
          <Box sx={{ mt: 1 }}>
            {unknownEmailsPending.map((email) => (
              <Typography key={email} variant="body2">
                - {email}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => sendOrderEmailRequest({ saveUnknown: false })}
            disabled={sendingOrderEmail}
          >
            {t("Samples.sendWithoutSaving")}
          </Button>
          <Button
            variant="contained"
            onClick={() => sendOrderEmailRequest({ saveUnknown: true })}
            disabled={sendingOrderEmail}
          >
            {t("Samples.saveAndSend")}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
